import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { manifestationTypeDescriptors } from '../../constants/descriptors';
import { isDescriptorActive, IDescriptorMappedForUI } from '../../utils/filters';
import { CheckboxButtonsMultiple } from '../../components/CheckboxButtonsMultiple';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersData } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';
import { IApplicationState } from '../../types/redux';
import { selectCanUseHiddenBase } from '../../selectors/user';

export const QuickFilterManifestationType: React.FC = React.memo(() => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);
  const canUseHiddenBase = useSelector(selectCanUseHiddenBase);

  const applyFilter = React.useCallback(() => {
    dispatch(getOffersData({ canUseHiddenBase }));
  }, [dispatch, canUseHiddenBase]);

  const stack = React.useMemo(
    () =>
      manifestationTypeDescriptors.reduce(
        (fits: IDescriptorMappedForUI[], { label, state }) =>
          state === '' ? fits : fits.concat({ value: state, text: label }),
        [],
      ),
    [],
  );

  const activeDescriptor = React.useMemo(
    () => manifestationTypeDescriptors.find(descriptor => isDescriptorActive(descriptor)(jsonQuery)),
    [jsonQuery],
  );

  /* istanbul ignore next */
  const value = activeDescriptor ? activeDescriptor.state : undefined;

  const handleChange = React.useCallback(
    (newValue: string) => {
      const currentValue = activeDescriptor && activeDescriptor.state;

      let wanted = newValue;
      /* istanbul ignore next */
      if (wanted === currentValue) {
        wanted = '';
      }

      const descriptor = manifestationTypeDescriptors.find(d => d.state === wanted);

      /* istanbul ignore else */
      if (descriptor) {
        dispatch({
          type: `${descriptor.name}/${descriptor.state}`,
          value: wanted,
        });
        dispatch(updateTags());
        applyFilter();
      }
    },
    [dispatch, activeDescriptor, applyFilter],
  );

  return <CheckboxButtonsMultiple stack={stack} onChange={handleChange} value={value} />;
});

QuickFilterManifestationType.displayName = 'QuickFilterManifestationType';
